/* eslint-disable no-unsafe-optional-chaining */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import FilterSelect from '../filters/FilterSelect';
import AutoCompleteSearch from '../input/AutoCompleteSearch';
import api from '../../api';
import { modifiedData } from '../../utils/modifiedDataProjects';
import { formatVersions, formatZDate, statusStyle } from '../../utils/general';

const projectTypes = [
  { id: 'All', name: 'All' },
  { id: 'Regular', name: 'Contract' },
  { id: 'Ad-Hoc', name: 'Project' }
];

export interface ProjectCardData {
  assignments: {
    assigned3Days: string;
    assignedLess10Days: string;
    assignedMore10Days: string;
    required3Days: string;
    requiredLess10Days: string;
    requiredMore10Days: string;
  };
  clientCompanyName: string;
  clientFirstName: string;
  clientLastName: string;
  contratctInNegotiationIdsInformation: {
    leadId: string;
    proposalId: string;
  };
  convertedByType: string;
  createdAt: string;
  firstVersionStartDate: string;
  id: string;
  leadDescription: string;
  leadName: string;
  leadProjectEndDate: string;
  leadProjectStartDate: string;
  operatorFirstName: string;
  operatorLastName: string;
  status: string;
  updatedAt: string;
  version: string;
}

export interface SearchBarProps {
  onTypeChange: (type: string) => void;
  searchText: string;
  setSearchText: (value: string) => void;
  typeSelected: string | null;
}

export interface ProjectCardProps {
  onClick: () => void;
  project: ProjectCardData;
}

function SearchBar({
  searchText,
  setSearchText,
  onTypeChange,
  typeSelected
}: SearchBarProps) {
  return (
    <Box
      display="flex"
      gap={5}
      justifyContent="left"
      sx={{ mb: 4, mt: 0, pl: 5 }}
    >
      <FilterSelect
        label="Type"
        onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
          onTypeChange(event.target.value as string)
        }
        options={projectTypes}
        value={typeSelected == null ? 'All' : typeSelected}
      />
      <AutoCompleteSearch
        options={[]}
        setOptions={() => {}}
        setSearch={
          setSearchText as React.Dispatch<React.SetStateAction<string>>
        }
        setValueSearched={setSearchText}
        value={searchText}
      />
      <Button onClick={() => setSearchText('')}>Clear</Button>
    </Box>
  );
}

function ProjectCard({ project, onClick }: ProjectCardProps) {
  const { t } = useTranslation();
  return (
    <Card sx={{ borderRadius: 2, cursor: 'pointer', width: '100%' }}>
      <CardContent>
        <Box
          display="flex"
          flexDirection="row"
          gap={1}
          justifyContent="space-between"
          width="100%"
        >
          <Typography variant="heading_400">{project.leadName}</Typography>
          <Chip
            label={
              <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
                width="100%"
              >
                <i className={`custom-icon icon-${project?.status}`} />
                <Typography sx={{ lineHeight: 1 }} variant="button_100">
                  {project.status}
                </Typography>
              </Box>
            }
            size="medium"
            style={{ ...statusStyle(project.status), padding: 8 }}
            variant="outlined"
          />
        </Box>
        <Typography variant="body1">{project.leadDescription}</Typography>
        <Box display="flex" flexDirection="row" gap={1} mt={1}>
          <Typography variant="heading_300">
            {t('LISTING_PAGES.dateRange')}:
          </Typography>
          <Typography variant="body_200">
            {`${project.leadProjectStartDate} - ${project.leadProjectEndDate}`}
          </Typography>
        </Box>
        {project.convertedByType !== null && (
          <Box display="flex" flexDirection="row" gap={1} mt={1}>
            <Typography variant="heading_300">
              {t('LISTING_PAGES.convertedBy')}:
            </Typography>
            <Typography variant="body_200">
              {project.convertedByType}
            </Typography>
            {project.convertedByType === 'Client' ? (
              <Typography variant="body_200">{`- ${project.clientFirstName} ${project.clientLastName}`}</Typography>
            ) : (
              <Typography variant="body_200">{`- ${project.operatorFirstName} ${project.operatorLastName}`}</Typography>
            )}
          </Box>
        )}
        <Box
          display="flex"
          flexDirection="row"
          gap={1}
          justifyContent="space-between"
          width="100%"
        >
          <Box display="flex" flexDirection="row" gap={1} mt={1}>
            <Typography variant="heading_300">
              {t('LISTING_PAGES.lastUpdated')}:
            </Typography>
            <Typography variant="body_200">
              {formatZDate(project.updatedAt)}
            </Typography>
          </Box>
          <Chip
            color="secondary"
            label={
              <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
                width="100%"
              >
                <Typography sx={{ lineHeight: 1 }} variant="button_100">
                  {`${formatVersions(Number(project.version))}`}
                </Typography>
              </Box>
            }
            size="medium"
            style={{ padding: 8 }}
            variant="outlined"
          />
        </Box>
        <Box display="flex" flexDirection="row" gap={1} mt={1}>
          <Typography variant="heading_300">
            {t('LISTING_PAGES.firstVersionStartDate')}:
          </Typography>
          <Typography variant="body_200">
            {formatZDate(project.firstVersionStartDate)}
          </Typography>
        </Box>
        <Button
          color="primary"
          onClick={onClick}
          sx={{ borderRadius: 2, height: 40, mt: 2, width: '100%' }}
          variant="outlined"
        >
          <Typography>Open</Typography>
        </Button>
      </CardContent>
    </Card>
  );
}

function ClientProjects({ id }: { id: string }) {
  const [fireAPIGetProjects, setFireAPIGetProjects] = useState<boolean>(false);
  const [type, setType] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [projects, setProjects] = useState<ProjectCardData[]>([]);
  const page = useRef<number>(1);
  const navigate = useNavigate();

  const {
    data: mainResponse,
    error,
    mutate
  } = useSWR(
    fireAPIGetProjects
      ? { id, items: 20, page: page.current, searchText, type }
      : null,
    api.ClientApi.clientGetProjectsByClientId
  );

  const onTypeChange = (selected: string) => {
    mutate(undefined, false);
    page.current = 1;
    setType(selected === 'All' ? null : selected);
    setProjects([]);
    setFireAPIGetProjects(true);
  };

  useEffect(() => {
    if (
      mainResponse?.data &&
      Array.isArray(mainResponse.data) &&
      mainResponse.data.length > 0
    ) {
      setFireAPIGetProjects(false);
      const newData = [
        ...(projects.length ? projects : []),
        ...modifiedData(mainResponse.data)
      ];
      setProjects(newData);
    } else if (
      mainResponse?.data &&
      Array.isArray(mainResponse.data) &&
      mainResponse.data.length === 0
    ) {
      setFireAPIGetProjects(false);
      setProjects([]);
    }
  }, [mainResponse]);

  useEffect(() => {
    mutate(undefined, false);
    page.current = 1;
    setProjects([]);
    setFireAPIGetProjects(true);
  }, [searchText]);

  useEffect(() => {
    setFireAPIGetProjects(true);
    return () => {
      mutate(undefined, false);
    };
  }, [id]);

  return (
    <Box>
      <SearchBar
        onTypeChange={onTypeChange}
        searchText={searchText}
        setSearchText={setSearchText}
        typeSelected={type}
      />
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        px={2}
        sx={{ justifyContent: 'flex-start' }}
      >
        {projects.map((project) => (
          <Box
            key={project.id}
            sx={{
              boxSizing: 'border-box',
              display: 'flex',
              mb: 0.5,
              width: '460px'
            }}
          >
            <ProjectCard
              onClick={() =>
                navigate(`/app/projects/detail/${project.id}`, {
                  state: { id: project.id, lastPage: `/app/crm/edit/${id}` }
                })
              }
              project={project}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default ClientProjects;
